import './client-job-info.scss';

import { useContext, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import type { TIeForm } from '@/components/App/Leasing/types';
import { EOrganizationTypesCode } from '@/components/Shared/constants/dicts-codes';
import {
  FormFieldSwitch,
  Input,
  MaskedInput,
  Select
} from '@/components/Shared/ui';
import { DictTypes } from '@/constants';
import { setDictionaryOptions } from '@/services';
import { formatPrice } from '@/services/formatter';
import { useAppSelector } from '@/store';

import { CreateTabContext } from '../../../IeOrder';

const ClientJobInfo: React.FC = () => {
  const { dicts } = useAppSelector((state) => state.dict);

  const { isFieldsFrozen } = useContext(CreateTabContext);
  const { control } = useFormContext<TIeForm>();

  const organizationTypes = useMemo(
    () =>
      setDictionaryOptions(DictTypes.OrganizationTypes, dicts)?.map((item) => ({
        value: String(item.value),
        label: String(item.label)
      })) || [],
    [dicts]
  );
  const socialStatuses = useMemo(
    () =>
      setDictionaryOptions(DictTypes.SocialStatuses, dicts)?.map((item) => ({
        value: String(item.value),
        label: String(item.label)
      })) || [],
    [dicts]
  );

  return (
    <div className="ie-client-job-info">
      <div className="ie-order-step__subtitle">Информация о работе клиента</div>

      <div className="ie-order-step__grid">
        <Controller
          control={control}
          name="companyName"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' }
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              value={field.value}
              label="Наименование организации"
              error={error?.message}
              isFrozen={isFieldsFrozen}
              isRequired
            />
          )}
        />
        <Select
          label="Вид организации"
          selectedValue={organizationTypes.find(
            (item) => item.value === String(EOrganizationTypesCode.IP)
          )}
          onChange={() => {}}
          options={organizationTypes}
          isFrozen
          isRequired
        />
        <Controller
          control={control}
          name="jobTitle"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' }
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              value={field.value}
              label="Должность"
              error={error?.message}
              isFrozen={isFieldsFrozen}
              isRequired
            />
          )}
        />

        {/* ------------------------------------------ */}

        <Controller
          control={control}
          name="jobAddress"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' }
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              value={field.value}
              label="Рабочий адрес"
              error={error?.message}
              isFrozen={isFieldsFrozen}
              isRequired
              autoComplete="true"
            />
          )}
        />
        <Controller
          control={control}
          name="salary"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' }
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              type="tel"
              value={field.value}
              label="Заработная плата"
              onChange={(e) => {
                const value = e.target.value.replace(/\s/g, '');
                if (value.match(/\D/)) return;
                field.onChange(formatPrice(value));
              }}
              error={error?.message}
              isFrozen={isFieldsFrozen}
              isRequired
            />
          )}
        />
        <Controller
          control={control}
          name="additionalSalary"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' }
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              type="tel"
              value={field.value}
              label="Дополнительный доход"
              onChange={(e) => {
                const value = e.target.value.replace(/\s/g, '');
                if (value.match(/\D/)) return;
                field.onChange(formatPrice(value));
              }}
              error={error?.message}
              isFrozen={isFieldsFrozen}
              isRequired
            />
          )}
        />

        {/* ------------------------------------------ */}

        <Controller
          control={control}
          name="workPhoneNumber"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: {
              value: '+7 (777) 666-55-44'.length,
              message: 'Неверный номер телефона'
            }
          }}
          render={({ field, fieldState: { error } }) => (
            <MaskedInput
              {...field}
              type="tel"
              value={field.value}
              label="Рабочий номер телефона"
              error={error?.message}
              mask="+7 (___) ___-__-__"
              replacement={{ _: /\d/ }}
              isFrozen={isFieldsFrozen}
              isRequired
            />
          )}
        />
        <Controller
          control={control}
          name="socialStatus"
          rules={{
            required: 'Пожалуйста, введите данные',
            minLength: { value: 1, message: 'Пожалуйста, введите данные' }
          }}
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              label="Социальный статус"
              selectedValue={socialStatuses.find(
                (item) => item.value === field.value
              )}
              onChange={(value) => field.onChange(value.value)}
              options={socialStatuses}
              error={error?.message}
              isFrozen={isFieldsFrozen}
              isRequired
            />
          )}
        />
        <FormFieldSwitch checked label="Владелец ИП" isFrozen />
      </div>
    </div>
  );
};

export default ClientJobInfo;
